<div *transloco="let t" class="-mx-4 -mt-8 -mb-14 lg:-mx-13.5 lg:-my-9 bg-surface-page min-h-screen">
  <div class="px-4 pt-8 pb-14 lg:px-13.5 lg:py-9">
    @if (isLoading()) {
    <juno-libs-ui-order-details-skeleton />
    } @else if (hasError()) {
    <div class="flex flex-col items-center justify-center min-h-[90dvh] gap-4 text-center">
      <img alt="" class="w-24 opacity-40" src="/images/icons/folder-bold.svg" />
      <p class="text-base font-medium text-ink-muted">
        {{ t('GENERAL.ERROR_MESSAGES.SOMETHING_WENT_WRONG') }}
      </p>
      <dss-libs-ui-button color="primary" variant="fill" label="HEADER.MANAGE_ORDER" (clickOutput)="goBack()" />
    </div>
    } @else if (order(); as order) {

    <!-- Page Header -->
    <div class="flex flex-col gap-4 mb-6">
      <button
        (click)="goBack()"
        class="self-start cursor-pointer flex items-center justify-center gap-1 text-ink-muted text-sm font-medium"
      >
        <mat-icon class="size-4! text-ink-muted! rtl:rotate-180" svgIcon="arrow-left-outline"></mat-icon>
        <span class="rtl:-translate-y-0.5">{{ t('GENERAL.BACK') }}</span>
      </button>

      <!-- Title row -->
      <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div class="min-w-0 flex-1">
          <h1 class="text-2xl lg:text-[1.75rem] font-semibold text-ink leading-snug">
            <span
              class="line-clamp-2 lg:line-clamp-none lg:inline-block lg:max-w-2xl lg:truncate lg:align-bottom"
              matTooltipPosition="right"
              [matTooltip]="order.name"
              matTooltipClass="tooltip-white"
              dssLibsUiTooltipIfTruncated
            >
              {{ order.name }}
            </span>
          </h1>
          <div class="flex flex-nowrap items-center gap-3 mt-2 overflow-x-auto no-scrollbar whitespace-nowrap">
            <span class="text-sm font-medium text-ink-hint">
              {{ t('ORDER-DETAILS.ORDER') }} {{ order.orderNumber | translocoDecimal : { useGrouping: false } }}
            </span>
            <div class="w-px h-4 bg-stroke-soft shrink-0"></div>
            <span class="text-sm font-medium text-ink-subtle">
              {{ t('ORDER-DETAILS.CREATED-BY') }}
              <span
                class="inline-block max-w-40 truncate align-bottom ms-1"
                [matTooltip]="order.userName"
                matTooltipPosition="right"
                matTooltipClass="tooltip-white"
                dssLibsUiTooltipIfTruncated
              >
                {{ order.userName }}
              </span>
            </span>
            <div class="w-px h-4 bg-stroke-soft shrink-0"></div>
            <span
              class="text-sm font-medium"
              [class.text-warning-ink]="isExpiringSoon()"
              [class.text-ink-subtle]="!isExpiringSoon()"
            >
              {{ t('ORDER-DETAILS.EXPIRES') }} {{ order.expiryDate | translocoDate : { dateStyle: 'medium' } }}
            </span>
          </div>
        </div>
        @if (canCancelOrder()) {
        <dss-libs-ui-button
          color="danger"
          variant="outline"
          label="ORDER-DETAILS.CANCEL-ORDER-MODAL.CANCEL-ORDER"
          [isProcessing]="isCancellingOrder()"
          (clickOutput)="openCancelOrderModal()"
          appliedClasses="w-full lg:w-auto"
        />
        }
      </div>
    </div>

    <!-- Two-Column Layout -->
    <div class="flex flex-col lg:flex-row gap-6 items-start">
      <!-- Left Column -->
      <div class="flex flex-col gap-4 flex-1 min-w-0 w-full">
        <!-- Order Status Card -->
        <div class="bg-white border border-surface-subtle rounded-lg overflow-hidden">
          <div class="bg-surface-card border-b border-surface-subtle flex items-center justify-between px-4 py-3.5">
            <span class="text-xs font-semibold text-primary uppercase tracking-widest">
              {{ t('ORDER-DETAILS.ORDER-STATUS') }}
            </span>

            <dss-libs-ui-chip
              [label]="t(OrderStatusTranslationMapper.get(order.status)!)"
              [color]="getOrderStatusBadgeClass(order.status!)"
            >
            </dss-libs-ui-chip>
          </div>

          <!-- Stats Row -->
          <div class="flex flex-wrap min-[76rem]:flex-nowrap border-b border-surface-subtle">
            <div
              class="flex flex-col gap-2 px-4 py-2 w-1/2 min-[76rem]:flex-1 border-r border-b min-[76rem]:border-b-0 border-surface-subtle"
            >
              <span class="text-[0.625rem] font-semibold text-ink-subtle uppercase tracking-wider">
                {{ t('ORDER-DETAILS.SENT') }}
              </span>
              <span class="text-lg font-semibold text-ink">
                {{ order.createdAt | translocoDate : { dateStyle: 'medium' } }}
              </span>
              <span class="text-xs text-ink-hint">
                {{ order.createdAt | translocoDate : { timeStyle: 'short' } }}
              </span>
            </div>
            <div
              [class]="
                hasReviewers()
                  ? 'flex flex-col gap-2 px-4 py-2 w-1/2 min-[76rem]:flex-1 min-[76rem]:border-r border-b min-[76rem]:border-b-0 border-surface-subtle'
                  : 'flex flex-col gap-2 px-4 py-2 w-full min-[76rem]:flex-1 order-3 min-[76rem]:order-2 min-[76rem]:border-r border-surface-subtle'
              "
            >
              <span class="text-[0.625rem] font-semibold text-ink-hint uppercase tracking-wider">
                {{ t('ORDER-DETAILS.EXPIRES') }}
              </span>
              <span
                class="text-lg font-semibold"
                [class.text-warning-ink]="isExpiringSoon()"
                [class.text-ink]="!isExpiringSoon()"
              >
                {{ order.expiryDate | translocoDate : { dateStyle: 'medium' } }}
              </span>
              @if (daysRemaining() !== null) {
              <span class="text-xs text-ink-hint">
                {{ daysRemaining()! | translocoDecimal }} {{ t('ORDER-DETAILS.DAYS-REMAINING') }}
              </span>
              }
            </div>
            <div
              [class]="
                hasReviewers()
                  ? 'flex flex-col gap-2 px-4 py-2 w-1/2 min-[76rem]:flex-1 border-r border-surface-subtle'
                  : 'flex flex-col gap-2 px-4 py-2 w-1/2 min-[76rem]:flex-1 order-2 min-[76rem]:order-3 border-b min-[76rem]:border-b-0 border-surface-subtle'
              "
            >
              <span class="text-[0.625rem] font-semibold text-ink-hint uppercase tracking-wider">
                {{ t('ORDER-DETAILS.SIGNED-BY') }}
              </span>
              <span class="text-lg font-semibold text-ink">
                {{ signedCount() | translocoDecimal }} {{ t('ORDER-DETAILS.OF') }}
                {{ totalSigners() | translocoDecimal }}
              </span>
              <span class="text-xs text-ink-hint">{{ t('ORDER-DETAILS.SIGNERS') }}</span>
            </div>
            @if (hasReviewers()) {
            <div class="flex flex-col gap-2 px-4 py-2 w-1/2 min-[76rem]:flex-1">
              <span class="text-[0.625rem] font-semibold text-ink-hint uppercase tracking-wider">
                {{ t('ORDER-DETAILS.REVIEWED-BY') }}
              </span>
              <span class="text-lg font-semibold text-ink">
                {{ reviewedCount() | translocoDecimal }} {{ t('ORDER-DETAILS.OF') }}
                {{ reviewers().length | translocoDecimal }}
              </span>
              <span class="text-xs text-ink-hint">{{ t('ORDER-DETAILS.REVIEWERS') }}</span>
            </div>
            }
          </div>

          <!-- Completion Progress -->
          @if (!isOrderRejected()) {
          <div class="px-4.5 py-3 flex flex-col gap-1.5">
            <div class="flex items-center justify-between text-xs">
              <span class="font-medium text-ink-muted">{{ t('ORDER-DETAILS.COMPLETION') }}</span>
              <span class="font-semibold text-ink">{{ completionPercent() | translocoDecimal }}%</span>
            </div>
            <dss-libs-ui-progress-bar [fillRatio]="completionRatio()" />
          </div>
          }
        </div>

        <!-- Activity Log Card -->
        <dss-libs-feature-activity-log-card [activities]="activities()" />
      </div>

      <!-- Right Column -->
      <div class="flex flex-col gap-4 w-full lg:w-90 lg:shrink-0">
        @if (tabs().length > 0) {
        <dss-libs-feature-recipients-panel
          [signers]="signers()"
          [reviewers]="reviewers()"
          [receivers]="receivers()"
          [tabs]="tabs()"
          [expiryDate]="order.expiryDate"
          [daysRemaining]="daysRemaining()"
          [sendingReminderForId]="sendingReminderForId()"
          [canSendReminder]="canSendReminder()"
          (sendReminder)="sendReminder($event)"
        />
        }

        <!-- Attachments Card -->
        @if (order.documents.length) {
        <dss-libs-feature-attachments-card [documents]="order.documents" [userName]="order.userName" />
        }
      </div>
    </div>
    }
  </div>
</div>
